import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Megaphone, ChevronRight, ChevronLeft, Sparkles, Coins, History, Zap, Info, Tag, Crown, Layers } from "lucide-react";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCredits } from "@/hooks/useCredits";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

import { ProductUpload } from "@/components/ad-studio/ProductUpload";
import { AdTypeSelector } from "@/components/ad-studio/AdTypeSelector";
import { PlatformSelector } from "@/components/ad-studio/PlatformSelector";
import { AdTextEditor, TextConfig } from "@/components/ad-studio/AdTextEditor";
import { AdStylePicker } from "@/components/ad-studio/AdStylePicker";
import { AdPreviewGallery } from "@/components/ad-studio/AdPreviewGallery";
import { AdTemplates, AD_TEMPLATES, AdTemplate } from "@/components/ad-studio/AdTemplates";

const STEPS = [
  { id: 1, title: "Template", icon: "🎯" },
  { id: 2, title: "Image Produit", icon: "📷" },
  { id: 3, title: "Plateformes", icon: "📱" },
  { id: 4, title: "Personnalisation", icon: "✍️" },
  { id: 5, title: "Génération", icon: "✨" },
];

// Optimized pricing: 5 credits per creative (was 10)
const CREDITS_PER_CREATIVE = 5;

// Batch generation options
const BATCH_OPTIONS = [
  { value: 1, label: "1 créative", discount: 0 },
  { value: 3, label: "3 créatives", discount: 10 },
  { value: 6, label: "6 créatives", discount: 20 },
];

interface Creative {
  platform: string;
  format: string;
  aspect_ratio: string;
  output_url: string;
}

export default function AdStudio() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { balance } = useCredits();

  const [currentStep, setCurrentStep] = useState(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedCreatives, setGeneratedCreatives] = useState<Creative[]>([]);
  const [batchCount, setBatchCount] = useState(1);

  // Form state
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [adName, setAdName] = useState("");
  const [productImage, setProductImage] = useState("");
  const [creativeType, setCreativeType] = useState("showcase");
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [textConfig, setTextConfig] = useState<TextConfig>({
    title: "",
    subtitle: "",
    cta: "",
    price: "",
    discount: "",
  });
  const [selectedStyle, setSelectedStyle] = useState("white-gradient");

  // Get batch discount
  const getBatchDiscount = () => {
    const option = BATCH_OPTIONS.find(o => o.value === batchCount);
    return option?.discount || 0;
  };

  const handleTemplateSelect = (template: AdTemplate) => {
    setSelectedTemplate(template.id);
    setCreativeType(template.creativeType);
    setSelectedStyle(template.style);
    setTextConfig({
      title: template.textConfig.title,
      subtitle: template.textConfig.subtitle,
      cta: template.textConfig.cta,
      price: template.textConfig.price || "",
      discount: template.textConfig.discount || "",
    });
    
    // Auto-advance if not custom template
    if (template.id !== "custom") {
      setTimeout(() => setCurrentStep(2), 300);
    }
  };

  const togglePlatform = (platform: string) => {
    setSelectedPlatforms((prev) =>
      prev.includes(platform)
        ? prev.filter((p) => p !== platform)
        : [...prev, platform]
    );
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return !!selectedTemplate;
      case 2:
        return !!productImage;
      case 3:
        return selectedPlatforms.length > 0;
      case 4:
        return !!selectedStyle && !!adName.trim();
      default:
        return true;
    }
  };

  // Calculate total credits with batch discount
  const estimatedCredits = useMemo(() => {
    const baseCost = selectedPlatforms.length * CREDITS_PER_CREATIVE * batchCount;
    const discount = getBatchDiscount();
    return Math.ceil(baseCost * (1 - discount / 100));
  }, [selectedPlatforms, batchCount]);

  // Original cost before discount (for display)
  const originalCredits = useMemo(() => {
    return selectedPlatforms.length * CREDITS_PER_CREATIVE * batchCount;
  }, [selectedPlatforms, batchCount]);

  const handleGenerate = async () => {
    if (!user) {
      toast({
        title: "Connexion requise",
        description: "Veuillez vous connecter pour créer des publicités",
        variant: "destructive",
      });
      return;
    }

    if (balance < estimatedCredits) {
      toast({
        title: "Crédits insuffisants",
        description: `Vous avez besoin de ${estimatedCredits} crédits pour générer ${selectedPlatforms.length} créative(s)`,
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    setGeneratedCreatives([]);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/generate-ad-creative`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({
            ad_name: adName,
            product_image_url: productImage,
            creative_type: creativeType,
            platforms: selectedPlatforms,
            text_config: textConfig,
            style: selectedStyle,
            batch_count: batchCount,
            use_floop_pro: true, // Force Floop Pro (Nano Banana)
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Erreur lors de la génération");
      }

      setGeneratedCreatives(data.creatives);
      toast({
        title: "Créatives générées !",
        description: `${data.creatives.length} créative(s) "${adName}" prête(s) à télécharger`,
      });
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <p className="text-muted-foreground">
              Choisissez un template prédéfini pour démarrer rapidement ou créez votre propre publicité personnalisée.
            </p>
            <AdTemplates selected={selectedTemplate} onSelect={handleTemplateSelect} />
          </div>
        );
      case 2:
        return (
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Uploadez l'image de votre produit. Utilisez une image de haute qualité avec un fond neutre pour de meilleurs résultats.
            </p>
            <ProductUpload imageUrl={productImage} onImageChange={setProductImage} />
          </div>
        );
      case 3:
        return (
          <div className="space-y-4">
            <p className="text-muted-foreground">
              Sélectionnez les plateformes pour lesquelles vous souhaitez générer des créatives. 
              <span className="text-primary font-medium"> {CREDITS_PER_CREATIVE} crédits par plateforme.</span>
            </p>
            <PlatformSelector selected={selectedPlatforms} onToggle={togglePlatform} />
            
            {/* Batch Generation */}
            {selectedPlatforms.length > 0 && (
              <div className="mt-6 p-4 bg-secondary/50 rounded-xl border border-border">
                <div className="flex items-center gap-2 mb-3">
                  <Layers className="w-4 h-4 text-primary" />
                  <h4 className="font-medium">Génération par lots</h4>
                  <Badge variant="outline" className="text-xs">Économisez jusqu'à 20%</Badge>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  Générez plusieurs variations pour chaque plateforme
                </p>
                <div className="flex gap-2">
                  {BATCH_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setBatchCount(option.value)}
                      className={`flex-1 p-3 rounded-xl border transition-all ${
                        batchCount === option.value
                          ? "border-primary bg-primary/10"
                          : "border-border hover:border-primary/50"
                      }`}
                    >
                      <div className="text-sm font-medium">{option.label}</div>
                      {option.discount > 0 && (
                        <div className="text-xs text-highlight font-semibold">-{option.discount}%</div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      case 4:
        return (
          <div className="space-y-6">
            {/* Ad Name */}
            <div className="space-y-2">
              <Label htmlFor="adName" className="flex items-center gap-2">
                <Tag className="w-4 h-4 text-primary" />
                Nom de la publicité <span className="text-destructive">*</span>
              </Label>
              <Input
                id="adName"
                placeholder="Ex: Promo Black Friday, Lancement Collection..."
                value={adName}
                onChange={(e) => setAdName(e.target.value)}
                className="max-w-md"
              />
              <p className="text-xs text-muted-foreground">
                Ce nom vous aidera à retrouver vos créatives dans l'historique
              </p>
            </div>

            {/* Creative Type */}
            <div>
              <h3 className="font-medium mb-3">Type de créative</h3>
              <AdTypeSelector selected={creativeType} onSelect={setCreativeType} />
            </div>

            {/* Style */}
            <div>
              <h3 className="font-medium mb-3">Style de fond</h3>
              <AdStylePicker selected={selectedStyle} onSelect={setSelectedStyle} />
            </div>
            
            {/* Text Config */}
            <div>
              <h3 className="font-medium mb-3">Textes de la publicité</h3>
              <AdTextEditor config={textConfig} onChange={setTextConfig} />
            </div>
          </div>
        );
      case 5:
        return (
          <div className="space-y-6">
            {!isGenerating && generatedCreatives.length === 0 && (
              <div className="space-y-4">
                <div className="p-4 bg-secondary/50 rounded-xl border border-border">
                  <h3 className="font-medium mb-3 flex items-center gap-2">
                    <Info className="w-4 h-4 text-primary" />
                    Récapitulatif de la commande
                  </h3>
                  <ul className="space-y-2 text-sm">
                    <li className="flex justify-between">
                      <span className="text-muted-foreground">Nom de la publicité</span>
                      <span className="font-medium truncate max-w-[200px]">{adName}</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-muted-foreground">Template</span>
                      <span className="font-medium">
                        {AD_TEMPLATES.find(t => t.id === selectedTemplate)?.name || "Personnalisé"}
                      </span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-muted-foreground">Type de créative</span>
                      <span className="font-medium capitalize">{creativeType}</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-muted-foreground">Plateformes</span>
                      <span className="font-medium">{selectedPlatforms.length} sélectionnée(s)</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-muted-foreground">Variations par plateforme</span>
                      <span className="font-medium">{batchCount}</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-muted-foreground">Total créatives</span>
                      <span className="font-medium">{selectedPlatforms.length * batchCount}</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-muted-foreground">Style</span>
                      <span className="font-medium">{selectedStyle}</span>
                    </li>
                    {textConfig.title && (
                      <li className="flex justify-between">
                        <span className="text-muted-foreground">Titre</span>
                        <span className="font-medium truncate max-w-[200px]">{textConfig.title}</span>
                      </li>
                    )}
                  </ul>
                </div>

                <div className="p-4 bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl border border-primary/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                        <Coins className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Coût total</p>
                        <div className="flex items-center gap-2">
                          <p className="text-xl font-bold text-primary">{estimatedCredits} crédits</p>
                          {getBatchDiscount() > 0 && (
                            <span className="text-sm text-muted-foreground line-through">{originalCredits}</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Votre solde</p>
                      <p className="text-lg font-semibold">{balance} crédits</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <Crown className="w-4 h-4 text-highlight" />
                    <p className="text-xs text-muted-foreground">
                      Propulsé par <span className="text-highlight font-medium">Floop Pro</span> pour une qualité studio
                    </p>
                    {getBatchDiscount() > 0 && (
                      <Badge variant="secondary" className="text-xs bg-highlight/20 text-highlight">
                        -{getBatchDiscount()}% batch
                      </Badge>
                    )}
                  </div>
                </div>

                <Button
                  size="lg"
                  className="w-full bg-gradient-floop hover:opacity-90 shadow-lg shadow-primary/30"
                  onClick={handleGenerate}
                  disabled={balance < estimatedCredits}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Générer {selectedPlatforms.length * batchCount} créative{selectedPlatforms.length * batchCount > 1 ? "s" : ""}
                </Button>

                {balance < estimatedCredits && (
                  <p className="text-center text-sm text-destructive">
                    Crédits insuffisants. <a href="/payment" className="underline">Acheter des crédits</a>
                  </p>
                )}
              </div>
            )}
            <AdPreviewGallery creatives={generatedCreatives} isLoading={isGenerating} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <AppLayout>
      <div className="container max-w-5xl py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center">
              <Megaphone className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold">Ad Creative Studio</h1>
                <Badge className="bg-accent text-accent-foreground">NOUVEAU</Badge>
              </div>
              <p className="text-muted-foreground">
                Créez des publicités à haute conversion pour tous les réseaux
              </p>
            </div>
          </div>
          <Button variant="outline" onClick={() => navigate("/ad-history")}>
            <History className="w-4 h-4 mr-2" />
            Historique
          </Button>
        </div>

        {/* Steps indicator */}
        <div className="flex items-center justify-between mb-8 overflow-x-auto pb-2">
          {STEPS.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <button
                onClick={() => step.id < currentStep && setCurrentStep(step.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                  currentStep === step.id
                    ? "bg-primary text-primary-foreground"
                    : currentStep > step.id
                    ? "bg-primary/20 text-primary cursor-pointer"
                    : "bg-secondary text-muted-foreground"
                }`}
              >
                <span>{step.icon}</span>
                <span className="hidden md:inline text-sm font-medium">{step.title}</span>
              </button>
              {index < STEPS.length - 1 && (
                <ChevronRight className="w-4 h-4 mx-2 text-muted-foreground" />
              )}
            </div>
          ))}
        </div>

        {/* Main content */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <span>{STEPS[currentStep - 1].icon}</span>
              {STEPS[currentStep - 1].title}
            </CardTitle>
          </CardHeader>
          <CardContent>{renderStepContent()}</CardContent>
        </Card>

        {/* Navigation */}
        {currentStep < 5 && (
          <div className="flex justify-between mt-6">
            <Button
              variant="outline"
              onClick={() => setCurrentStep((prev) => Math.max(1, prev - 1))}
              disabled={currentStep === 1}
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Précédent
            </Button>
            <Button
              onClick={() => setCurrentStep((prev) => Math.min(5, prev + 1))}
              disabled={!canProceed()}
            >
              Suivant
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
